import { useMemo, useRef, useState } from 'react';
import { tr, trf } from '../lib/i18n';
import { C } from '../theme/palette';
import { _inp } from './ui/Inputs.jsx';

/** Première lettre d'un nom, sans accent, pour ranger la liste. */
const lettreDe = (nom) =>
  (
    String(nom || '')
      .trim()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .charAt(0)
      .toUpperCase() || '#'
  ).replace(/[^A-Z]/, '#');

/** Un client tout juste créé a un identifiant provisoire (une chaîne). */
const memeId = (a, b) => a != null && b != null && b !== '' && String(a) === String(b);

/** Le NPA et la ville, s'ils disent quelque chose (pas un simple « . »). */
const lieu = (c) => (/[\p{L}\p{N}]/u.test(String(c.npa || '')) ? c.npa : '');

const sansAccent = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Le choix du client d'une offre ou d'une facture, sans la longue liste de
 * tous les clients : une recherche, les derniers clients servis et, sur
 * demande, tous les clients par ordre alphabétique dans une boîte qu'on fait
 * défiler au doigt (avec les lettres pour sauter). Une fois choisi, le client
 * tient dans une carte verte avec « Changer ». Supprimer un client se fait
 * depuis la page Clients, plus d'ici.
 *
 * @param {object} props propriétés
 * @param {object[]} props.clients tous les clients
 * @param {*} props.clientId client choisi
 * @param {(client: object) => void} props.onChoisir reçoit le client touché
 * @param {() => void} props.onChanger revient au choix (client retiré)
 * @param {Array<*>} [props.recents] identifiants des derniers clients servis
 * @returns {import('react').ReactElement} le choix ou la carte du client
 */
export function ChoixClient({ clients, clientId, onChoisir, onChanger, recents = [] }) {
  const [q, setQ] = useState('');
  const [tous, setTous] = useState(false);
  const boite = useRef(null);
  const liste = useMemo(() => clients || [], [clients]);
  const choisi = liste.find((c) => memeId(c.id, clientId));

  const trouves = useMemo(() => {
    const n = sansAccent(q.trim());
    if (!n) return [];
    return (
      liste
        .filter((c) => sansAccent([c.nom, c.npa, c.adr, c.tel, c.email].join(' ')).includes(n))
        // Assez pour ne jamais cacher un client cherché : huit seulement
        // laissaient le neuvième introuvable tant qu'on tapait.
        .slice(0, 60)
    );
  }, [q, liste]);

  const derniers = useMemo(() => {
    const vus = [];
    for (const id of recents) {
      const c = liste.find((x) => memeId(x.id, id));
      if (c && !vus.includes(c)) vus.push(c);
      if (vus.length === 3) break;
    }
    if (vus.length < 3) {
      for (const c of [...liste].reverse()) {
        if (!vus.includes(c)) vus.push(c);
        if (vus.length === 3) break;
      }
    }
    return vus;
  }, [recents, liste]);

  const parLettre = useMemo(() => {
    const tri = [...liste].sort((a, b) => sansAccent(a.nom).localeCompare(sansAccent(b.nom), 'fr'));
    const groupes = [];
    for (const c of tri) {
      const l = lettreDe(c.nom);
      const g = groupes.at(-1);
      if (g && g.lettre === l) g.clients.push(c);
      else groupes.push({ lettre: l, clients: [c] });
    }
    return groupes;
  }, [liste]);

  const choisir = (c) => {
    setQ('');
    setTous(false);
    onChoisir(c);
  };

  if (choisi) {
    return (
      <div
        style={{
          border: '2px solid ' + C.green,
          background: C.greenL,
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{choisi.nom}</div>
          {lieu(choisi) && <div style={{ fontSize: 9, color: C.grey }}>{lieu(choisi)}</div>}
        </div>
        <span aria-hidden="true" style={{ color: C.green, fontSize: 16, fontWeight: 900 }}>
          ✓
        </span>
        <button
          type="button"
          onClick={onChanger}
          style={{
            border: 'none',
            background: 'none',
            color: C.teal,
            fontSize: 10.5,
            fontWeight: 800,
            cursor: 'pointer',
            padding: '6px 2px',
          }}
        >
          {tr('Changer')}
        </button>
      </div>
    );
  }

  const ligne = (c) => (
    <button
      key={c.id}
      type="button"
      onClick={() => choisir(c)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: '1px solid ' + C.border,
        borderRadius: 10,
        padding: '9px 11px',
        marginBottom: 5,
        background: C.white,
        cursor: 'pointer',
      }}
    >
      <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.ink }}>{c.nom}</span>
      {lieu(c) && <span style={{ fontSize: 8.5, color: C.muted }}>{lieu(c)}</span>}
    </button>
  );

  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={tr('🔎 Chercher un client (nom, ville, téléphone)…')}
        aria-label={tr('Chercher un client')}
        style={{ ..._inp, marginBottom: 8 }}
      />
      {q.trim() ? (
        trouves.length ? (
          trouves.map(ligne)
        ) : (
          <div style={{ fontSize: 9.5, color: C.grey, padding: '2px 2px 8px' }}>
            {trf('Aucun client « {0} ». Ajoute-le avec le bouton ci-dessous.', q.trim())}
          </div>
        )
      ) : (
        <>
          {derniers.length > 0 && (
            <div
              style={{
                fontSize: 8.5,
                fontWeight: 800,
                color: C.muted,
                letterSpacing: 0.4,
                margin: '2px 2px 6px',
              }}
            >
              {tr('DERNIERS CLIENTS')}
            </div>
          )}
          {derniers.map(ligne)}
          {liste.length > derniers.length && (
            <button
              type="button"
              onClick={() => setTous((v) => !v)}
              aria-expanded={tous}
              style={{
                width: '100%',
                border: '1px solid ' + C.border,
                background: C.white,
                color: C.navy,
                borderRadius: 10,
                padding: '9px 10px',
                fontSize: 10.5,
                fontWeight: 800,
                cursor: 'pointer',
                margin: '2px 0 8px',
              }}
            >
              {trf('📋 Tous mes clients ({0})', liste.length)} {tous ? '▲' : '▼'}
            </button>
          )}
          {tous && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 6 }}>
                {parLettre.map((g) => (
                  <button
                    key={g.lettre}
                    type="button"
                    aria-label={trf('Aller à la lettre {0}', g.lettre)}
                    onClick={() => {
                      const el = boite.current?.querySelector(`[data-lettre="${g.lettre}"]`);
                      if (el && boite.current) boite.current.scrollTop = el.offsetTop;
                    }}
                    style={{
                      minWidth: 24,
                      height: 24,
                      border: 'none',
                      borderRadius: 6,
                      background: C.navyXL,
                      color: C.navy,
                      fontSize: 10,
                      fontWeight: 900,
                      cursor: 'pointer',
                    }}
                  >
                    {g.lettre}
                  </button>
                ))}
              </div>
              <div
                ref={boite}
                role="group"
                aria-label={tr('Tous mes clients')}
                style={{
                  position: 'relative',
                  maxHeight: 300,
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  border: '1px solid ' + C.border,
                  borderRadius: 10,
                  marginBottom: 8,
                  background: C.white,
                }}
              >
                {parLettre.map((g) => (
                  <div key={g.lettre} data-lettre={g.lettre}>
                    <div
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: C.navyXL,
                        padding: '4px 11px',
                        fontSize: 9.5,
                        fontWeight: 900,
                        color: C.grey,
                      }}
                    >
                      {g.lettre}
                    </div>
                    {g.clients.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => choisir(c)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          borderBottom: '1px solid ' + C.navyL,
                          background: C.white,
                          padding: '9px 11px',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.ink }}
                        >
                          {c.nom}
                        </span>
                        {lieu(c) && (
                          <span style={{ fontSize: 8.5, color: C.muted }}>{lieu(c)}</span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
